import { Edit2, Eye } from 'react-feather';
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { EditorProps } from '../../common/Tiptap/types';
import Address from './Address';
import UI from '../../common/UI';
import useLocale from '../../common/useLocale';
import useProject from '../useProject';

export interface MailEditorLayoutProps {
  editor: (props: Partial<EditorProps>) => ReactNode;
  value: string | null;
  event: {
    title: string;
    logo?: {
      url: string;
    } | null;
    locale: string;
    brand_color?: string | null;
  }
  instructions?: {
    title: string;
    instructions: string;
  };
  showRegistrationSummary?: boolean;
  showOrderSummary?: boolean;
}

const MailEditorLayout = ({
  editor, value, event, instructions, showRegistrationSummary, showOrderSummary,
}: MailEditorLayoutProps) => {
  const { t } = useTranslation();
  const { formatCurrency } = useLocale();
  const project = useProject();

  const [tab, setTab] = useState<'editor' | 'preview'>('editor');

  const tKey = (key: string) => `mail_editor.${event.locale}.${key}`;

  const hasValue = value && value !== '<p></p>';

  return (
    <>
      <UI.Div sc={{ mb: 2 }}>
        <UI.Pill>
          <UI.Button
            onClick={() => setTab('editor')}
            sc={{ size: 'sm', px: 2, outline: true, active: tab === 'editor' }}
          >
            <UI.Icon>
              <Edit2 />
            </UI.Icon>
            {' '}
            {t('edit')}
          </UI.Button>
          <UI.Button
            onClick={() => setTab('preview')}
            sc={{
              size: 'sm', px: 2, outline: true, active: tab === 'preview', color: !hasValue ? 'gray.400' : undefined,
            }}
            disabled={!hasValue}
            style={{ opacity: 1 }}
          >
            <UI.Icon>
              <Eye />
            </UI.Icon>
            {' '}
            {t('preview')}
          </UI.Button>
        </UI.Pill>
      </UI.Div>
      {tab === 'editor' && editor({
        maxContentWidth: 560,
        brandColor: event.brand_color,
      })}
      {tab === 'preview' && (
        <PreviewContainer brandColor={event.brand_color}>
          <UI.Div sc={{ borderRadius: 'md' }} style={{ border: '1px solid rgba(0, 0, 0, .1)' }}>
            <UI.FormGrid sc={{ py: 6, px: 4, gutter: 1.5 }} style={{ margin: '0 auto', maxWidth: 560 + 2 * 24 }}>
              <UI.Div sc={{ textAlign: 'center' }}>
                {event.logo && (
                  <img src={event.logo.url} alt={event.title} height={80} />
                )}
                {!event.logo && (
                  <UI.H3 sc={{ fontWeight: 500 }}>
                    {event.title}
                  </UI.H3>
                )}
              </UI.Div>
              <UI.HTML html={value} />
              {instructions && (
                <>
                  <UI.HR />
                  <UI.Div>
                    <UI.H4 sc={{ strong: true }}>
                      {instructions.title}
                    </UI.H4>
                    <UI.HTML html={instructions.instructions} />
                  </UI.Div>
                </>
              )}
              {showRegistrationSummary && (
                <UI.Div sc={{ background: 'gray.100', padding: 4, borderRadius: 'xl' }}>
                  <UI.H2 sc={{ mb: 1 }}>
                    {t(tKey('your_dashboard'))}
                  </UI.H2>
                  <UI.Paragraph sc={{ mb: 3 }}>
                    {t(tKey('your_dashboard_description'))}
                  </UI.Paragraph>
                  <UI.Div>
                    <UI.Button sc={{ brand: 'secondary' }} style={{ pointerEvents: 'none' }}>
                      {t(tKey('open_dashboard'))}
                    </UI.Button>
                  </UI.Div>
                </UI.Div>
              )}
              {showOrderSummary && (
                <>
                  <UI.HR />
                  <UI.Div>
                    <UI.H2 sc={{ mb: 1 }}>
                      {t(tKey('your_order'))}
                    </UI.H2>
                    <UI.Div sc={{ mb: 3 }}>
                      {t(tKey('invoice_number'))}
                    </UI.Div>
                    <UI.Table
                      sc={{
                        borderedRows: true, borderWidth: 1, spacingV: 1,
                      }}
                      width="100%"
                    >
                      <UI.TBody>
                        <UI.TR>
                          <UI.TD>
                            {t(tKey('ticket'))}
                          </UI.TD>
                          <UI.TD sc={{ textAlign: 'right' }}>
                            {formatCurrency(1000)}
                          </UI.TD>
                        </UI.TR>
                        <UI.TR sc={{ strong: true }}>
                          <UI.TD>
                            {t(tKey('total'))}
                          </UI.TD>
                          <UI.TD sc={{ textAlign: 'right' }}>
                            {formatCurrency(1000)}
                          </UI.TD>
                        </UI.TR>
                      </UI.TBody>
                    </UI.Table>
                  </UI.Div>
                </>
              )}
              <UI.HR />
              <UI.Div
                sc={{ textAlign: 'center', small: true }}
                style={{ borderBottomLeftRadius: '6px', borderBottomRightRadius: '6px' }}
              >
                <UI.Div sc={{ color: 'gray.500', mb: 3 }}>
                  {t(tKey('organizer'))}
                </UI.Div>
                <UI.Strong>
                  {project.organisation_name}
                </UI.Strong>
                <UI.Div>
                  <Address
                    street={project.organisation_address}
                    zip_code={project.organisation_zip_code}
                    city={project.organisation_city}
                    country={project.organisation_country}
                  />
                </UI.Div>
                <UI.Div>
                  {project.organisation_email}
                </UI.Div>
              </UI.Div>
            </UI.FormGrid>
          </UI.Div>
        </PreviewContainer>
      )}
    </>
  );
};

const PreviewContainer = styled.div<{ brandColor?: string | null; }>`
  ${({ theme, brandColor }) => css`
    .button, button {
      background: ${brandColor || theme.colors.secondary[500]} !important;
      color: ${theme.getTitleColor(brandColor || theme.colors.secondary[500])} !important;
    }
  `}
`;

export default MailEditorLayout;
